import React, { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import {
  Container, Row, Col, Form, Table, Button,
  Card, Spinner
} from "react-bootstrap";
import {
  FaDownload, FaUser,
  FaChartBar
} from "react-icons/fa";
import { MdOutlineEventBusy } from "react-icons/md";
import axios from "axios";
import html2canvas from "html2canvas";
import { io } from "socket.io-client";
import "./ViewAttendance.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const COMP_OFF_COLOR = "#9370DB";
const LEAVE_COLOR = "#FF6B6B";

// ─── Day name → getDay() index ───────────────────────────────────────────────
const DAY_NAME_TO_INDEX = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

// Returns getDay() index for an employee's weekly_off string
// e.g. "Sunday" → 0, "Saturday" → 6
function getWeeklyOffIndex(weeklyOff) {
  return DAY_NAME_TO_INDEX[(weeklyOff || "Sunday").trim().toLowerCase()] ?? 0;
}

// Is the given JS Date object the weekly off day for this employee?
function isEmployeeWeeklyOff(dateObj, weeklyOff) {
  return dateObj.getDay() === getWeeklyOffIndex(weeklyOff);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatWorkHour(decimalHours) {
  const totalMinutes = Math.round(decimalHours * 60);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hrs}.${mins < 10 ? "0" : ""}${mins}`;
}

function buildThreshold(record) {
  if (record.ci_time) {
    return moment(`${moment(record.date).format("YYYY-MM-DD")} ${record.ci_time}`, "YYYY-MM-DD hh:mm A");
  }
  return moment(record.in_time, "YYYY-MM-DD").set({ hour: 10, minute: 0, second: 0 });
}

function applyLateMarkLogic(record) {
  if (
    record.in_time &&
    record.out_time &&
    record.day === "Full Day" &&
    !(record.location && record.location.toLowerCase().includes("sv"))
  ) {
    const recordDate = new Date(record.date);
    // Don't apply late mark on weekly off day
    const weeklyOffIdx = getWeeklyOffIndex(record.weekly_off_name);
    if (recordDate.getDay() !== weeklyOffIdx) {
      const checkIn = moment(record.in_time, "YYYY-MM-DD HH:mm:ss");
      const threshold = buildThreshold(record);
      if (checkIn.isAfter(threshold)) {
        record.day = "Late Mark";
      }
    }
  }
}

function areSameDate(date1, date2) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

// ─── getDisplayForRecord ─────────────────────────────────────────────────────
// weeklyOff = employee's weekly_off string e.g. "Sunday" / "Saturday"
const getDisplayForRecord = (record, compensatoryOffData = [], leaveData = [], weeklyOff = "Sunday") => {
  const validCodes = ["ro", "mo", "rso", "do", "wfh"];

  // 1) Full Day Leave
  if (leaveData && leaveData.length > 0) {
    const recordDateFormatted = moment(record.date).format("YYYY-MM-DD");
    const leave = leaveData.find(lv => {
      if (!lv || lv.leave_day !== "Full") return false;
      const empNameFromLeave = lv.Name || lv.emp_name || lv.employee_name;
      if (!empNameFromLeave || !record.emp_name) return false;
      return (
        moment(lv.leave_date).format("YYYY-MM-DD") === recordDateFormatted &&
        empNameFromLeave.toString().trim() === record.emp_name.toString().trim()
      );
    });
    if (leave) {
      return { text: "L", style: { backgroundColor: LEAVE_COLOR, color: "#fff", fontWeight: "bold" } };
    }
  }

  // 2) Compensatory Off
  if (compensatoryOffData && compensatoryOffData.length > 0) {
    const recordDateFormatted = moment(record.date).format("YYYY-MM-DD");
    const compOff = compensatoryOffData.find(co => {
      if (!co || co.status !== "approved") return false;
      const empNameFromCo = co.Name || co.emp_name || co.employee_name;
      if (!empNameFromCo || !record.emp_name) return false;
      return (
        moment(co.compensatory_off_date).format("YYYY-MM-DD") === recordDateFormatted &&
        empNameFromCo.toString().trim() === record.emp_name.toString().trim()
      );
    });
    if (compOff) {
      return { text: "CO", style: { backgroundColor: COMP_OFF_COLOR, color: "#fff", fontWeight: "bold" } };
    }
  }

  const locationText = record.location ? record.location.toLowerCase().trim() : "";
  const locationWords = locationText.split(/\s+/);
  const hasValidCode = locationWords.some(w => validCodes.includes(w));
  const isSiteVisit = record.location && !hasValidCode;

  const hasWorked = record.in_time && record.out_time;
  const recordDate = new Date(record.date);

  // Use passed weeklyOff OR the one stored on the record by the API
  const effectiveWeeklyOff = record.weekly_off_name || weeklyOff;
  const isWeeklyOff = isEmployeeWeeklyOff(recordDate, effectiveWeeklyOff);

  // worked_on_off_day is set by backend; also detect locally
  const isWorkedOffDay =
    record.worked_on_off_day ||
    (hasWorked && (isWeeklyOff || record.day === "Holiday"));

  // Site visit on weekly off / holiday
  if (isWorkedOffDay && isSiteVisit) {
    return {
      text: (!record.in_time || !record.out_time) ? "SV.I" : "SV.P",
      style: { backgroundColor: "#FFFF00" },
    };
  }

  // Worked on weekly off or holiday (non site visit)
  if (isWorkedOffDay && hasWorked && !isSiteVisit) {
    if (record.full_request === 1 || record.full_request === "1") {
      return { text: "P", style: { backgroundColor: "#28a745", color: "#fff" } };
    }
    if (record.work_hour >= 8.5) {
      // Apply late mark only on worked weekly off day (not on holidays)
      if (isWeeklyOff) {
        const checkIn = moment(record.in_time, "YYYY-MM-DD HH:mm:ss");
        const threshold = buildThreshold(record);
        if (checkIn.isAfter(threshold)) {
          return {
            text: <span style={{ textDecoration: "underline" }}>P</span>,
            style: { backgroundColor: "#28a745", color: "#fff" },
          };
        }
      }
      return { text: "P", style: { backgroundColor: "#28a745", color: "#fff" } };
    } else if (record.work_hour >= 5) {
      return { text: "H", style: { backgroundColor: "#B0E0E6" } };
    } else {
      return { text: "AB", style: { backgroundColor: "#ffffff", color: "#000", fontWeight: "bold" } };
    }
  }

  // Holiday not worked → red
  if (record.day === "Holiday" && !hasWorked) {
    return { text: "", style: { backgroundColor: "#ff0000", color: "#fff" } };
  }

  // Weekly off not worked → orange
  if (isWeeklyOff && !hasWorked) {
    return { text: "", style: { backgroundColor: "#ff9900", color: "#fff" } };
  }

  // Late mark logic for regular days
  if (
    record.in_time &&
    record.out_time &&
    record.day === "Full Day" &&
    !(record.location && record.location.toLowerCase().includes("sv"))
  ) {
    if (!isWeeklyOff) {
      const checkIn = moment(record.in_time, "YYYY-MM-DD HH:mm:ss");
      const threshold = buildThreshold(record);
      if (checkIn.isAfter(threshold)) {
        record.day = "Late Mark";
      }
    }
  }

  // Incomplete attendance check
  if (
    !isSiteVisit &&
    record.day !== "Absent" &&
    (!record.work_hour || record.work_hour >= 5) &&
    (!record.in_time || !record.out_time)
  ) {
    return { text: "I", style: { backgroundColor: "#ffffff", color: "#000" } };
  }

  // Site visit logic
  if (record.day !== "Holiday" && !isWeeklyOff && record.location) {
    if (!hasValidCode) {
      return {
        text: (!record.in_time || !record.out_time) ? "SV.I" : "SV.P",
        style: { backgroundColor: "#FFFF00" },
      };
    }
  }

  // Work less than 5 hours
  if (record.day !== "Absent") {
    if (record.full_request === 1 || record.full_request === "1") {
      return { text: "P", style: { backgroundColor: "#28a745", color: "#fff" } };
    }
    if (record.work_hour !== undefined && record.work_hour < 5) {
      return { text: "AB", style: { backgroundColor: "#ffffff", color: "#000", fontWeight: "bold" } };
    }
  }

  switch (record.day) {
    case "Full Day":
      return { text: "P", style: { backgroundColor: "#28a745", color: "#fff" } };
    case "Half Day":
      return { text: "H", style: { backgroundColor: "#B0E0E6" } };
    case "Late Mark":
      return {
        text: <span style={{ textDecoration: "underline" }}>P</span>,
        style: { backgroundColor: "#28a745", color: "#fff" },
      };
    case "Absent":
      return { text: "A", style: { backgroundColor: "#FFC0CB" } };
    default:
      return { text: "", style: {} };
  }
};

// ─── Component ───────────────────────────────────────────────────────────────
const ViewAttendance = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const empId = storedUser.employeeId || "";

  const [socket, setSocket] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [viewMode, setViewMode] = useState("monthwise");
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [attendanceData, setAttendanceData] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [locationFilter, setLocationFilter] = useState("All");
  const [locationOptions, setLocationOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [compensatoryOffData, setCompensatoryOffData] = useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [saturdayOffData, setSaturdayOffData] = useState([]);

  // Map: empName → weekly_off string (built from attendanceData)
  // e.g. { "Sumedha Thakur Desai": "Saturday", "Kalpesh Urunkar": "Sunday" }
  const [empWeeklyOffMap, setEmpWeeklyOffMap] = useState({});

  const attendanceRef = useRef(null);

  // Fetch employees
  useEffect(() => {
    setIsLoading(true);
    axios
      .get(`${API_URL}/api/employees`, { params: { empId } })
      .then((response) => {
        const sorted = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
        setEmployees(sorted);
      })
      .catch((err) => console.error("Error fetching employees:", err))
      .finally(() => setIsLoading(false));
  }, []);

  // Fetch holidays
  useEffect(() => {
    axios
      .get(`${API_URL}/api/holidays/holidays`)
      .then((res) => setHolidays(res.data))
      .catch((err) => console.error("Error fetching holidays:", err));
  }, []);

  // Fetch attendance
  const fetchAttendance = useCallback(() => {
    setIsLoading(true);
    const params = { viewMode };
    if (selectedEmployee) params.empName = selectedEmployee;
    if (viewMode === "datewise" && selectedDate) params.date = selectedDate;
    if (viewMode === "monthwise") {
      params.month = selectedMonth;
      params.year = selectedYear;
    }
    if (storedUser.employeeId) params.employeeId = storedUser.employeeId;
    if (storedUser.name) params.name = storedUser.name;

    axios
      .get(`${API_URL}/api/attendance/view`, { params })
      .then((response) => {
        const att  = response.data.attendance || response.data || [];
        const comp = response.data.compensatoryOff || [];
        const lv   = response.data.leaves || [];
        const sat  = response.data.saturdayOff || [];

        setAttendanceData(att);
        setCompensatoryOffData(comp);
        setLeaveData(lv);
        setSaturdayOffData(sat);

        // Build empName → weekly_off map from API response
        // API now returns weekly_off_name on every record
        const woMap = {};
        att.forEach((r) => {
          if (r.emp_name && r.weekly_off_name) {
            woMap[r.emp_name.trim()] = r.weekly_off_name;
          }
        });
        setEmpWeeklyOffMap(woMap);

        const locs = Array.from(new Set(att.map((r) => r.location).filter(Boolean)));
        setLocationOptions(["All", ...locs.sort()]);
      })
      .catch((err) => console.error("Error fetching attendance:", err))
      .finally(() => setIsLoading(false));
  }, [viewMode, selectedEmployee, selectedDate, selectedMonth, selectedYear,
      storedUser.employeeId, storedUser.name]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  // WebSocket
  useEffect(() => {
    const s = io(API_URL);
    setSocket(s);
    return () => s.disconnect();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const onAtt = () => fetchAttendance();
    const onHol = () => {
      axios.get(`${API_URL}/api/holidays/holidays`).then((r) => setHolidays(r.data)).catch(console.error);
      fetchAttendance();
    };
    socket.on("attendanceChanged", onAtt);
    socket.on("holidayChanged", onHol);
    return () => { socket.off("attendanceChanged", onAtt); socket.off("holidayChanged", onHol); };
  }, [socket, fetchAttendance]);

  // Download
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(attendanceRef.current, {
        scale: 2, useCORS: true, backgroundColor: "#f8fafc", logging: false,
        onclone: (doc) => {
          const btn = doc.getElementById("downloadReport");
          if (btn) btn.style.display = "none";
        },
      });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png", 1.0);
      link.download = `attendance_${viewMode}_${moment().format("YYYY-MM-DD_HH-mm")}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error generating image", err);
    } finally {
      setIsDownloading(false);
    }
  };

  // Saturday off helper
  const isSaturdayOff = (empName, year, month, day) => {
    const dateStr = `${year}-${String(parseInt(month)).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return saturdayOffData.some((record) => {
      const name = record.employee_name || record.Name;
      if (!name || !record.off_dates) return false;
      return (
        name.toString().trim() === empName.toString().trim() &&
        record.off_dates.split(",").includes(dateStr)
      );
    });
  };

  // ── groupAttendanceByDay ────────────────────────────────────────────────────
  const groupAttendanceByDay = () => {
    const pivotData = {};

    // Ensure all employees are present in pivotData even if there is no attendance record for the month.
    // Weekly off will fall back to Sunday when not available.
    if (Array.isArray(employees) && employees.length > 0) {
      employees.forEach((emp) => {
        const empName = emp?.name || emp?.emp_name || "";
        if (!empName) return;
        const weeklyOff = empWeeklyOffMap?.[empName.trim()] || "Sunday";
        if (!pivotData[empName]) {
          pivotData[empName] = {
            days: {},
            presentDays: 0,
            lateMarkCount: 0,
            totalHours: 0,
            daysWorked: 0,
            weekly_off: weeklyOff,
          };
        }
      });
    }

    // Build pivot from attendance records
    attendanceData.forEach((rec) => {
      applyLateMarkLogic(rec);

      const emp = rec.emp_name;
      if (!emp) return;

      // Get this employee's weekly off
      const effectiveWeeklyOff = rec.weekly_off_name || empWeeklyOffMap[rec.emp_name?.trim()] || "Sunday";

      if (!pivotData[emp]) {
        pivotData[emp] = {
          days: {},
          presentDays: 0,
          lateMarkCount: 0,
          totalHours: 0,
          daysWorked: 0,
          weekly_off: effectiveWeeklyOff,
        };
      }

      const recDate = new Date(rec.date);
      const dayNum  = recDate.getDate();

      if (!pivotData[emp].days[dayNum]) {
        pivotData[emp].days[dayNum] = {
          work_hour:        Number(rec.work_hour) || 0,
          in_time:          rec.in_time || "",
          out_time:         rec.out_time || "",
          day:              rec.day,
          location:         rec.location || "",
          date:             rec.date,
          emp_name:         rec.emp_name,
          worked_on_off_day: rec.worked_on_off_day || false,
          full_request:     rec.full_request || 0,
          weekly_off_name:  effectiveWeeklyOff,
          ci_time:          rec.ci_time || "",
        };
      } else {
        const cell = pivotData[emp].days[dayNum];
        cell.work_hour += Number(rec.work_hour) || 0;

        if (rec.in_time) {
          if (
            !cell.in_time ||
            moment(rec.in_time, "YYYY-MM-DD HH:mm:ss").isBefore(
              moment(cell.in_time, "YYYY-MM-DD HH:mm:ss")
            )
          ) { cell.in_time = rec.in_time; }
        }

        if (rec.out_time) {
          if (
            !cell.out_time ||
            moment(rec.out_time, "YYYY-MM-DD HH:mm:ss").isAfter(
              moment(cell.out_time, "YYYY-MM-DD HH:mm:ss")
            )
          ) { cell.out_time = rec.out_time; }
        }

        if (rec.day === "Late Mark") cell.day = "Late Mark";
        if (rec.location && rec.location !== cell.location) {
          cell.location = cell.location
            ? (cell.location.includes(rec.location) ? cell.location : cell.location + ", " + rec.location)
            : rec.location;
        }
        if (rec.worked_on_off_day) cell.worked_on_off_day = true;
        if (rec.full_request === 1 || rec.full_request === "1") cell.full_request = 1;
        if (rec.ci_time) cell.ci_time = rec.ci_time;
      }
    });

    // Overlay compensatory off
    compensatoryOffData.forEach((co) => {
      if (!co || co.status !== "approved") return;
      const coDate   = moment(co.compensatory_off_date);
      const dayNum   = coDate.date();
      const coMonth  = coDate.month() + 1;
      const coYear   = coDate.year();
      if (coMonth !== parseInt(selectedMonth) || coYear !== parseInt(selectedYear)) return;
      const empName = co.Name || co.emp_name || co.employee_name;
      if (!empName) return;

      if (!pivotData[empName]) {
        pivotData[empName] = {
          days: {},
          presentDays: 0,
          lateMarkCount: 0,
          totalHours: 0,
          daysWorked: 0,
          weekly_off: empWeeklyOffMap?.[empName.trim()] || "Sunday",
        };
      }

      const existing = pivotData[empName].days[dayNum];
      if (existing) {
        if (existing.day === "SV.P") {
          pivotData[empName].presentDays -= 1;
          pivotData[empName].daysWorked  -= 1;
          pivotData[empName].totalHours  -= existing.work_hour;
        } else if (existing.day !== "Absent" && existing.work_hour >= 5) {
          const dec = existing.day === "Half Day" ? 0.5 : 1;
          pivotData[empName].presentDays -= dec;
          pivotData[empName].daysWorked  -= dec;
          pivotData[empName].totalHours  -= existing.work_hour;
        }
      }

      pivotData[empName].days[dayNum] = {
        work_hour: 8.5, in_time: "", out_time: "",
        day: "Comp Off", location: "", date: coDate.format("YYYY-MM-DD"),
        isCompOff: true, displayStatus: "Comp Off",
      };
      pivotData[empName].presentDays += 1;
      pivotData[empName].daysWorked  += 1;
      pivotData[empName].totalHours  += 8.5;
    });

    // Overlay leave (full day)
    leaveData.forEach((leave) => {
      if (!leave || leave.leave_day !== "Full") return;
      const leaveDate  = moment(leave.leave_date);
      const dayNum     = leaveDate.date();
      const leaveMonth = leaveDate.month() + 1;
      const leaveYear  = leaveDate.year();
      if (leaveMonth !== parseInt(selectedMonth) || leaveYear !== parseInt(selectedYear)) return;
      const empName = leave.Name || leave.emp_name || leave.employee_name;
      if (!empName) return;

      if (!pivotData[empName]) {
        pivotData[empName] = { days: {}, presentDays: 0, lateMarkCount: 0, totalHours: 0, daysWorked: 0, weekly_off: "Sunday" };
      }

      const existing = pivotData[empName].days[dayNum];
      if (existing) {
        if (existing.day === "SV.P") {
          pivotData[empName].presentDays -= 1;
          pivotData[empName].daysWorked  -= 1;
          pivotData[empName].totalHours  -= existing.work_hour;
        } else if (existing.day !== "Absent" && existing.work_hour >= 5) {
          const dec = existing.day === "Half Day" ? 0.5 : 1;
          pivotData[empName].presentDays -= dec;
          pivotData[empName].daysWorked  -= dec;
          pivotData[empName].totalHours  -= existing.work_hour;
        }
      }

      pivotData[empName].days[dayNum] = {
        work_hour: 0, in_time: "", out_time: "",
        day: "Leave", location: "", date: leaveDate.format("YYYY-MM-DD"),
        isLeave: true, displayStatus: "Leave",
      };
    });

    // Compute displayStatus per cell
    Object.keys(pivotData).forEach((emp) => {
      const empWeeklyOff = pivotData[emp].weekly_off || "Sunday";
      const weeklyOffIdx = getWeeklyOffIndex(empWeeklyOff);

      Object.keys(pivotData[emp].days).forEach((dayKey) => {
        const rec = pivotData[emp].days[dayKey];
        if (rec.isLeave)   { rec.displayStatus = "Leave";    rec.day = "Leave";    return; }
        if (rec.isCompOff) { rec.displayStatus = "Comp Off"; rec.day = "Comp Off"; return; }

        const hasWorked   = rec.in_time && rec.out_time;
        const recordDate  = new Date(rec.date);
        // Use per-cell weekly_off_name if present, else fall back to row-level
        const cellWeeklyOff = rec.weekly_off_name || empWeeklyOff;
        const isWeeklyOff   = isEmployeeWeeklyOff(recordDate, cellWeeklyOff);

        const isWorkedOffDay =
          rec.worked_on_off_day ||
          (hasWorked && (isWeeklyOff || rec.day === "Holiday"));

        const validCodes   = ["ro", "mo", "rso", "do", "wfh"];
        const locText      = rec.location ? rec.location.toLowerCase().trim() : "";
        const locWords     = locText.split(/\s+/);
        const hasValidCode = locWords.some((w) => validCodes.includes(w));
        const isSiteVisit  = rec.location && !hasValidCode;

        if (isWorkedOffDay && isSiteVisit) {
          rec.displayStatus = rec.day; // SV.P or SV.I
          return;
        }

        if (isWorkedOffDay && hasWorked && !isSiteVisit) {
          if (rec.full_request === 1 || rec.full_request === "1") {
            rec.displayStatus = "Full Day";
          } else if (rec.work_hour < 5) {
            rec.displayStatus = "AB";
          } else if (rec.work_hour < 8.5) {
            rec.displayStatus = "Half Day";
          } else {
            // Late mark only on weekly off days (not holidays), skip if incomplete
            if (isWeeklyOff && rec.out_time) {
              const checkIn   = moment(rec.in_time, "YYYY-MM-DD HH:mm:ss");
              const threshold = buildThreshold(rec);
              rec.displayStatus = checkIn.isAfter(threshold) ? "Late Mark" : "Full Day";
            } else {
              rec.displayStatus = "Full Day";
            }
          }
          rec.day = rec.displayStatus;
          return;
        }

        // Regular day
        if (rec.in_time && !["SV.I", "SV.P", "Absent", "Holiday"].includes(rec.day) && !isWeeklyOff) {
          const isIncomplete = !rec.in_time || !rec.out_time;
          if (isSiteVisit) {
            rec.displayStatus = isIncomplete ? "SV.I" : "SV.P";
          } else if (isIncomplete) {
            rec.displayStatus = "I";
          } else if (rec.full_request === 1 || rec.full_request === "1") {
            const checkIn   = moment(rec.in_time, "YYYY-MM-DD HH:mm:ss");
            const threshold = buildThreshold(rec);
            rec.displayStatus = checkIn.isAfter(threshold) ? "Late Mark" : "Full Day";
          } else if (rec.work_hour < 5) {
            rec.displayStatus = "AB";
          } else if (rec.work_hour < 8.5) {
            rec.displayStatus = "Half Day";
          } else {
            const checkIn   = moment(rec.in_time, "YYYY-MM-DD HH:mm:ss");
            const threshold = buildThreshold(rec);
            rec.displayStatus = checkIn.isAfter(threshold) ? "Late Mark" : "Full Day";
          }
          rec.day = rec.displayStatus;
        } else {
          rec.displayStatus = rec.day;
        }
      });
    });

    // Summarize
    Object.keys(pivotData).forEach((emp) => {
      Object.keys(pivotData[emp].days).forEach((dayKey) => {
        const cur = pivotData[emp].days[dayKey];
        if (cur.isCompOff) return;
        if (cur.day === "SV.P") {
          pivotData[emp].presentDays += 1;
          pivotData[emp].daysWorked  += 1;
          pivotData[emp].totalHours  += cur.work_hour;
        } else if (
          cur.day !== "Absent" && cur.day !== "Holiday" &&
          cur.day !== "Leave" && cur.work_hour >= 5
        ) {
          // Only count if NOT on weekly off day (SO shown but not counted as present)
          const recordDate  = new Date(cur.date);
          const cellWeeklyOff = cur.weekly_off_name || pivotData[emp].weekly_off || "Sunday";
          const isWeeklyOff   = isEmployeeWeeklyOff(recordDate, cellWeeklyOff);
          if (!isWeeklyOff || cur.worked_on_off_day) {
            if (cur.day === "Half Day") {
              pivotData[emp].presentDays += 0.5;
              pivotData[emp].daysWorked  += 0.5;
            } else {
              pivotData[emp].presentDays += 1;
              pivotData[emp].daysWorked  += 1;
            }
            pivotData[emp].totalHours += cur.work_hour;
          }
        }
        if (cur.day === "Late Mark") pivotData[emp].lateMarkCount++;
      });
    });

    return pivotData;
  };

  // ── renderMonthwiseTable ────────────────────────────────────────────────────
  const renderMonthwiseTable = () => {
    const daysInMonth = new Date(selectedYear, parseInt(selectedMonth, 10), 0).getDate();
    const pivotData   = groupAttendanceByDay();
    const todayStart  = new Date();
    todayStart.setHours(0, 0, 0, 0);

    return (
      <div style={{ overflowX: "auto", padding: 4 }}>
        <Table bordered hover size="sm"
          style={{ tableLayout: "fixed", fontSize: "0.75rem", minWidth: "900px" }}
          className="mb-0 custom-border-table"
        >
          <thead style={{ fontSize: "0.75rem" }}>
            <tr>
              <th style={{ width: "180px", position: "sticky", top: 0, background: "white", zIndex: 2 }}>Employee Name</th>
              <th style={{ width: "60px", textAlign: "center", position: "sticky", top: 0, background: "white", zIndex: 2 }}>Present</th>
              <th style={{ width: "60px", textAlign: "center", position: "sticky", top: 0, background: "white", zIndex: 2 }}>Late</th>
              <th style={{ width: "60px", textAlign: "center", position: "sticky", top: 0, background: "white", zIndex: 2 }}>AvgHr</th>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
                <th key={d} style={{ width: "30px", textAlign: "center", position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody style={{ fontSize: "0.75rem" }}>
            {Object.keys(pivotData).sort((a, b) => a.localeCompare(b)).map((emp) => {
              const rowData = pivotData[emp];
              // Employee's weekly off (from pivot, populated during grouping)
              const empWeeklyOff  = rowData.weekly_off || empWeeklyOffMap[emp.trim()] || "Sunday";
              const weeklyOffIdx  = getWeeklyOffIndex(empWeeklyOff);

              let avgHours = "0.00";
              if (rowData.daysWorked > 0) {
                const avg      = rowData.totalHours / rowData.daysWorked;
                const totalMins = Math.round(avg * 60);
                const hrs  = Math.floor(totalMins / 60);
                const mins = totalMins % 60;
                avgHours = `${hrs}.${mins < 10 ? "0" : ""}${mins}`;
              }

              return (
                <tr key={emp}>
                  <td style={{ width: "180px" }}>{emp}</td>
                  <td style={{ width: "60px", textAlign: "center" }}>{rowData.presentDays}</td>
                  <td style={{ width: "60px", textAlign: "center" }}>{rowData.lateMarkCount}</td>
                  <td style={{ width: "60px", textAlign: "center" }}>{avgHours}</td>

                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const dayNumber = i + 1;
                    const cellDate  = new Date(selectedYear, parseInt(selectedMonth, 10) - 1, dayNumber);
                    const dayOfWeek = cellDate.getDay();

                    // ── Is this cell the employee's weekly off? ────────────────
                    const isCellWeeklyOff = dayOfWeek === weeklyOffIdx;

                    const holidayFound = holidays.find((h) => areSameDate(new Date(h.holiday_date), cellDate));
                    const isSatOff     = dayOfWeek === 6 && isSaturdayOff(emp, selectedYear, selectedMonth, dayNumber);

                    let forcedStyle = {};
                    let cellText    = "";

                    // Leave check
                    const hasLeave = leaveData?.some((leave) => {
                      if (!leave || leave.leave_day !== "Full") return false;
                      const ld   = moment(leave.leave_date);
                      const eName = leave.Name || leave.emp_name || leave.employee_name;
                      if (!eName) return false;
                      return (
                        ld.date() === dayNumber &&
                        ld.month() + 1 === parseInt(selectedMonth) &&
                        ld.year() === parseInt(selectedYear) &&
                        eName.toString().trim() === emp.toString().trim()
                      );
                    });

                    // Comp off check
                    const hasCompOff = compensatoryOffData?.some((co) => {
                      if (!co || co.status !== "approved") return false;
                      const cd   = moment(co.compensatory_off_date);
                      const eName = co.Name || co.emp_name || co.employee_name;
                      if (!eName) return false;
                      return (
                        cd.date() === dayNumber &&
                        cd.month() + 1 === parseInt(selectedMonth) &&
                        cd.year() === parseInt(selectedYear) &&
                        eName.toString().trim() === emp.toString().trim()
                      );
                    });

                    if (hasLeave) {
                      forcedStyle = { backgroundColor: LEAVE_COLOR, color: "#fff", fontWeight: "bold" };
                      cellText = "L";
                    } else if (hasCompOff) {
                      forcedStyle = { backgroundColor: COMP_OFF_COLOR, color: "#fff", fontWeight: "bold" };
                      cellText = "CO";
                    } else {
                      const rec      = rowData.days[dayNumber];
                      const hasWorked = rec && rec.in_time && rec.out_time;
                      const validCodes = ["ro", "mo", "rso", "do", "wfh"];
                      const locText  = rec?.location ? rec.location.toLowerCase().trim() : "";
                      const locWords = locText.split(/\s+/);
                      const hasVC    = locWords.some((w) => validCodes.includes(w));
                      const isSV     = rec?.location && !hasVC;

                      // ── HOLIDAY ───────────────────────────────────────────────
                      if (holidayFound) {
                        if (hasWorked && isSV) {
                          const d = getDisplayForRecord({ ...rec, day: rec.displayStatus, worked_on_off_day: true }, compensatoryOffData, leaveData, empWeeklyOff);
                          cellText = d.text; forcedStyle = { ...d.style };
                        } else if (hasWorked && !isSV) {
                          const d = getDisplayForRecord({ ...rec, day: rec.displayStatus, worked_on_off_day: true }, compensatoryOffData, leaveData, empWeeklyOff);
                          cellText = d.text; forcedStyle = { ...d.style };
                        } else {
                          forcedStyle = { backgroundColor: "#ff0000", color: "#fff" };
                          cellText = "";
                        }
                      }
                      // ── EMPLOYEE'S WEEKLY OFF DAY ─────────────────────────────
                      else if (isCellWeeklyOff) {
                        if (hasWorked && isSV) {
                          const d = getDisplayForRecord({ ...rec, day: rec.displayStatus, worked_on_off_day: true }, compensatoryOffData, leaveData, empWeeklyOff);
                          cellText = d.text; forcedStyle = { ...d.style };
                        } else if (hasWorked && !isSV) {
                          const d = getDisplayForRecord({ ...rec, day: rec.displayStatus, worked_on_off_day: true }, compensatoryOffData, leaveData, empWeeklyOff);
                          cellText = d.text; forcedStyle = { ...d.style };
                        } else {
                          // Not worked on weekly off → orange SO
                          forcedStyle = { backgroundColor: "#ff9900", color: "#fff" };
                          cellText = "";
                        }
                      }
                      // ── REGULAR WORKING DAY ───────────────────────────────────
                      else {
                        const isPastMissing = !rec && cellDate < todayStart && !isCellWeeklyOff && !holidayFound;
                        if (isPastMissing) {
                          forcedStyle = { backgroundColor: "#FFC0CB" };
                          cellText = "A";
                        } else if (rec) {
                          const d = getDisplayForRecord({ ...rec, day: rec.displayStatus }, compensatoryOffData, leaveData, empWeeklyOff);
                          cellText = d.text; forcedStyle = { ...d.style };
                        }
                      }
                    }

                    // Saturday off override
                    if (isSatOff && !hasLeave && !hasCompOff) {
                      forcedStyle = { backgroundColor: "#dc3545", color: "#fff", fontWeight: "bold" };
                      cellText = "SO";
                    }

                    return (
                      <td key={dayNumber}
                        style={{ width: "30px", textAlign: "center", verticalAlign: "middle", padding: "2px 1px", ...forcedStyle }}
                      >
                        {cellText}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // ── renderDatewiseTable ─────────────────────────────────────────────────────
  const renderDatewiseTable = () => {
    const sortedData = attendanceData
      .filter((rec) => locationFilter === "All" || rec.location === locationFilter)
      .sort((a, b) => a.emp_name.localeCompare(b.emp_name));

    return (
      <div style={{ overflowX: "auto", border: "1px solid #000" }}>
        <Table bordered hover size="sm" style={{ fontSize: "0.75rem", minWidth: "800px", border: "1px solid #000" }}>
          <thead style={{ fontSize: "0.75rem" }}>
            <tr>
              <th>Employee</th><th>Date</th><th>In Time</th><th>Out Time</th>
              <th>Work Hr</th><th>Day</th>
              <th>
                <Form.Select size="sm" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)} style={{ fontSize: "0.75rem" }}>
                  {locationOptions.map((loc, i) => <option key={i} value={loc}>{loc}</option>)}
                </Form.Select>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((rec, idx) => {
              const empWeeklyOff = rec.weekly_off_name || empWeeklyOffMap[rec.emp_name?.trim()] || "Sunday";
              const dayDisplay   = getDisplayForRecord(rec, compensatoryOffData, leaveData, empWeeklyOff);
              const recordDate   = new Date(rec.date);
              const isWeeklyOff  = isEmployeeWeeklyOff(recordDate, empWeeklyOff);
              const hasWorked    = rec.in_time && rec.out_time;

              let fullText = "";
              if (dayDisplay.text === "L")         fullText = "Leave (Full Day)";
              else if (dayDisplay.text === "CO")   fullText = "Compensatory Off";
              else if (dayDisplay.text === "I")    fullText = "Incomplete Attendance";
              else if (dayDisplay.text === "SV.I") fullText = "Site Visit Incomplete";
              else if (dayDisplay.text === "SV.P") fullText = "Site Visit Present";
              else if (rec.location && !rec.location.toLowerCase().trim().split(/\s+/).some((w) => ["ro","mo","rso","do","wfh"].includes(w))) {
                fullText = (!rec.in_time || !rec.out_time) ? "Site Visit Incomplete" : "Site Visit Present";
              } else {
                switch (rec.day) {
                  case "Holiday": fullText = hasWorked ? "Worked on Holiday" : "Holiday"; break;
                  case "Full Day": fullText = "Full Day"; break;
                  case "Half Day": fullText = "Half Day"; break;
                  case "Late Mark": fullText = "Full Day (Late Mark)"; break;
                  case "Absent": fullText = "Absent"; break;
                  default:
                    if (isWeeklyOff) fullText = hasWorked ? `Worked on ${empWeeklyOff}` : empWeeklyOff;
                    else fullText = rec.day;
                }
              }

              return (
                <tr key={idx}>
                  <td>{rec.emp_name}</td>
                  <td>{moment(rec.date).format("YYYY-MM-DD")}</td>
                  <td>{rec.in_time}</td>
                  <td>{rec.out_time}</td>
                  <td>{rec.work_hour != null ? formatWorkHour(Number(rec.work_hour)) : ""}</td>
                  <td style={dayDisplay.style}>{fullText}</td>
                  <td>{rec.location}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="py-4 px-3 px-md-4" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <div ref={attendanceRef}>
        <Card className="shadow-sm border-0 mb-3">
          <Card.Body className="p-3">
            <Row className="g-3 align-items-center">
              <Col xs={12} sm={6} md={4} lg={2} className="mb-2 mb-md-0">
                <div className="btn-group w-100" role="group">
                  <Button variant={viewMode === "monthwise" ? "primary" : "outline-primary"} onClick={() => setViewMode("monthwise")} size="sm" className="px-2 py-2" style={{ fontSize: "0.85rem", height: "36px" }}>Monthwise</Button>
                  <Button variant={viewMode === "datewise" ? "primary" : "outline-primary"} onClick={() => setViewMode("datewise")} size="sm" className="px-2 py-2" style={{ fontSize: "0.85rem", height: "36px" }}>Datewise</Button>
                </div>
              </Col>

              <Col xs={12} sm={6} md={4} lg={2} className="mb-2 mb-md-0">
                <div className="d-flex align-items-center">
                  <FaUser size={16} className="me-2 text-muted" />
                  <Form.Select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} size="sm" className="py-2" style={{ fontSize: "0.85rem", height: "36px" }}>
                    <option value="">All</option>
                    {employees.map((emp, i) => <option key={i} value={emp.name}>{emp.name}</option>)}
                  </Form.Select>
                </div>
              </Col>

              <Col xs={12} md={12} lg={8} className="mt-1 mt-lg-0">
                <div className="d-flex align-items-center flex-wrap">
                  <div className="d-flex align-items-center me-3 mb-1">
                    <FaChartBar size={14} className="me-1 text-muted" />
                    <small className="fw-medium text-muted" style={{ fontSize: "0.85rem" }}>Legend:</small>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {[
                      { color: "#28a745", label: "P", tooltip: "Full Day (8.5 Hrs)", textColor: "#fff" },
                      { color: "#B0E0E6", label: "H", tooltip: "Half Day (5 Hrs)" },
                      { color: "#FFC0CB", label: "A", tooltip: "Absent" },
                      { color: "#ff9900", label: "", tooltip: "Weekly Off", textColor: "#fff" },
                      { color: "#28a745", label: "P", tooltip: "Late Mark", underline: true, textColor: "#fff" },
                      { color: "#ff0000", label: "", tooltip: "Holiday", textColor: "#fff" },
                      { color: "#FFFF00", label: "SV", tooltip: "Site Visit" },
                      { color: "#FFC0CB", label: "AB", tooltip: "Working < 5 Hrs" },
                      { color: COMP_OFF_COLOR, label: "CO", tooltip: "Compensatory Off", textColor: "#fff" },
                      { color: LEAVE_COLOR, label: "L", tooltip: "Leave (Full Day)", textColor: "#fff" },
                      { color: "#dc3545", label: "SO", tooltip: "Saturday Off", textColor: "#fff" },
                    ].map((item, index) => (
                      <div key={index} className="d-flex align-items-center me-2 mb-1" title={item.tooltip}>
                        <div className="d-flex align-items-center justify-content-center me-1"
                          style={{ width: "24px", height: "24px", backgroundColor: item.color, fontSize: "0.8rem", fontWeight: "bold", borderRadius: "3px", color: item.textColor || "#000" }}>
                          {item.underline ? <span style={{ textDecoration: "underline" }}>{item.label}</span> : item.label}
                        </div>
                        <small className="text-muted" style={{ fontSize: "0.85rem" }}>{item.tooltip}</small>
                      </div>
                    ))}
                    <div className="d-flex align-items-center me-2 mb-1" title="Incomplete Attendance">
                      <div className="d-flex align-items-center justify-content-center me-1"
                        style={{ width: "24px", height: "24px", backgroundColor: "#ffffff", fontSize: "0.8rem", fontWeight: "bold", border: "1.5px solid #dc3545", borderRadius: "3px", color: "#dc3545" }}>
                        I
                      </div>
                      <small className="text-danger fw-medium" style={{ fontSize: "0.85rem" }}>Incomplete</small>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mt-3 align-items-center">
              <Col xs={12} md={6} className="mb-2 mb-md-0">
                <div className="d-flex align-items-center">
                  <span className="me-2 text-muted fw-medium" style={{ fontSize: "0.85rem" }}>
                    {viewMode === "monthwise" ? "Select Month & Year:" : "Select Date:"}
                  </span>
                  {viewMode === "monthwise" ? (
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          setSelectedMonth((m) => {
                            let mm = parseInt(m, 10) - 1;
                            if (mm < 1) {
                              mm = 12;
                              setSelectedYear((y) => parseInt(y, 10) - 1);
                            }
                            return mm.toString();
                          });
                        }}
                        size="sm"
                        style={{ height: "36px" }}
                      >
                        &larr;
                      </Button>

                      <span className="mx-2 fw-semibold ea-nav-label" style={{ fontSize: "0.95rem" }}>
                        {moment(new Date(parseInt(selectedYear, 10), parseInt(selectedMonth, 10) - 1, 1)).format("MMM YYYY")}
                      </span>

                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          setSelectedMonth((m) => {
                            let mm = parseInt(m, 10) + 1;
                            if (mm > 12) {
                              mm = 1;
                              setSelectedYear((y) => parseInt(y, 10) + 1);
                            }
                            return mm.toString();
                          });
                        }}
                        size="sm"
                        style={{ height: "36px" }}
                      >
                        &rarr;
                      </Button>
                    </div>
                  ) : (
                    <Form.Control type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} size="sm" className="py-2" style={{ fontSize: "0.85rem", width: "160px", height: "36px" }} />
                  )}
                </div>
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-end">
                <div style={{ width: "160px" }}>
                  <Button onClick={handleDownload} variant="success" className="w-100 py-2 d-flex align-items-center justify-content-center" size="sm" disabled={isDownloading} style={{ fontSize: "0.85rem", height: "36px" }}>
                    {isDownloading ? (<><span className="spinner-border spinner-border-sm me-1"></span>Generating...</>) : (<><FaDownload size={14} className="me-1" />Download Report</>)}
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {isLoading ? (
          <Card className="shadow-sm border-0">
            <Card.Body className="text-center py-5">
              <Spinner animation="border" variant="primary" className="me-3" />
              <span>Loading attendance data...</span>
            </Card.Body>
          </Card>
        ) : (
          <>
            <Row className="g-0">
              <Col style={{ fontSize: "0.75rem" }}>
                {viewMode === "datewise" && renderDatewiseTable()}
                {viewMode === "monthwise" && renderMonthwiseTable()}
              </Col>
            </Row>
            {attendanceData.length === 0 && !isLoading && (
              <Card className="shadow-sm border-0 mt-4">
                <Card.Body className="text-center py-5">
                  <div className="display-1 text-muted mb-3"><MdOutlineEventBusy /></div>
                  <h5 className="text-muted mb-3">No attendance records found</h5>
                  <p className="text-muted mb-0">Try adjusting your filters or check back later</p>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default ViewAttendance;